// ============================================================
// TMR Portal Admin — the person form: values, validation and the request payloads
// ============================================================
import { isEmail, isValidCPF, maskCPF, onlyDigits } from '../../lib/format';
import { displayPhone, isValidInternationalPhone, toE164Phone } from '../../lib/phone';
import type {
  CreatePartyRequest,
  PartyCompanyLink,
  PartyOption,
  PartyRecord,
  PartyRole,
  UpdatePartyRequest,
} from './contract';

/** The person form's fields, held as the operator typed them — CPF and phone stay masked on screen. */
export interface PartyFormValues {
  name: string;
  cpf: string;
  email: string;
  phone: string;
  note: string;
}

/** Field-level messages, keyed by the field they belong to. */
export type PartyFormErrors = Partial<Record<keyof PartyFormValues, string>>;

/** An empty form — the starting point of a registration with nothing to prefill. */
export function emptyPartyForm(): PartyFormValues {
  return { name: '', cpf: '', email: '', phone: '', note: '' };
}

/** The form as it opens over an existing record, with the stored digits masked back for reading. */
export function toFormValues(record: PartyRecord): PartyFormValues {
  return {
    name: record.name,
    cpf: record.cpf ? maskCPF(record.cpf) : '',
    email: record.email ?? '',
    phone: displayPhone(record.phone),
    note: record.note ?? '',
  };
}

/** The form as it opens over a person the Empresa cadastre already has on screen. */
export function toFormValuesFromOption(option: Pick<PartyOption, 'name' | 'cpf' | 'email' | 'phone'>): PartyFormValues {
  return {
    name: option.name,
    cpf: option.cpf ? maskCPF(option.cpf) : '',
    email: option.email ?? '',
    phone: displayPhone(option.phone),
    note: '',
  };
}

/**
 * Checks a person the way the aggregate does (`Party.ValidateDetails`): the name is required, and CPF and
 * e-mail are optional but validated when present.
 *
 * Requiring more would reject records that are perfectly usable — the legacy keeps these people as loose,
 * mostly-empty rows, and a representative whose CPF nobody ever recorded is still the person who signs.
 * The server checks the same things; this only spares a round-trip.
 */
export function validateParty(values: PartyFormValues): PartyFormErrors {
  const errors: PartyFormErrors = {};

  if (!values.name.trim()) errors.name = 'Informe o nome da pessoa.';
  if (values.cpf.trim() && !isValidCPF(values.cpf)) errors.cpf = 'CPF inválido.';
  if (values.email.trim() && !isEmail(values.email)) errors.email = 'E-mail inválido.';
  if (!isValidInternationalPhone(values.phone)) errors.phone = 'Telefone inválido.';

  return errors;
}

/** Whether validation found nothing — the form may be submitted. */
export function isPartyFormValid(errors: PartyFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/**
 * `POST /parties` from the form.
 *
 * @param values What the operator filled in.
 * @param companies Associations to set at creation; omitted when there is no Company to attach to yet —
 *   the Empresa cadastre creates people before the Empresa itself has an id.
 */
export function toCreateRequest(values: PartyFormValues, companies?: PartyCompanyLink[]): CreatePartyRequest {
  return {
    name: values.name.trim(),
    cpf: optionalDigits(values.cpf),
    email: optional(values.email),
    phone: toE164Phone(values.phone),
    note: optional(values.note),
    ...(companies ? { companies } : {}),
  };
}

/**
 * `PUT /parties/{id}` from an edited form, over the record it was opened from.
 *
 * The endpoint replaces the whole record, so the archive flag and the associations are carried through
 * from what was read — a payload that only described the fields on screen would archive nobody and
 * **unlink everybody** (`Party.ReplaceCompanyLinks` clears whatever the request leaves out).
 */
export function toUpdateRequest(record: PartyRecord, values: PartyFormValues): UpdatePartyRequest {
  return {
    name: values.name.trim(),
    cpf: optionalDigits(values.cpf),
    email: optional(values.email),
    phone: toE164Phone(values.phone),
    note: optional(values.note),
    active: record.active,
    companies: record.companies,
  };
}

/** `PUT /parties/{id}` that only archives or restores — every other field comes back untouched. */
export function withActive(record: PartyRecord, active: boolean): UpdatePartyRequest {
  return { ...toRequest(record), active };
}

/** `PUT /parties/{id}` that only replaces the associations — the details come back untouched. */
export function withCompanies(record: PartyRecord, companies: PartyCompanyLink[]): UpdatePartyRequest {
  return { ...toRequest(record), companies };
}

/** PT-BR label for a position. Falls back to the raw value, so a role added server-side still reads. */
export function partyRoleLabel(role: string): string {
  switch (role) {
    case 'Representative':
      return 'Representante legal';
    case 'FocalContact':
      return 'Contato focal';
    default:
      return role;
  }
}

/** The positions the association editor offers, in the order the cadastre collects them. */
export const PARTY_ROLES: PartyRole[] = ['Representative', 'FocalContact'];

/** The record as the update payload, before the one field a caller means to change. */
function toRequest(record: PartyRecord): UpdatePartyRequest {
  return {
    name: record.name,
    cpf: record.cpf,
    email: record.email,
    phone: record.phone,
    note: record.note,
    active: record.active,
    companies: record.companies,
  };
}

/** Trimmed text, or null when the operator left the field empty — the backend reads absent as unknown. */
function optional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** The same for a masked field, sent as digits. */
function optionalDigits(value: string): string | null {
  const digits = onlyDigits(value);
  return digits === '' ? null : digits;
}
